/**
 * "What is the system doing right now?" as one line for the top of the
 * dashboard — derived entirely from data the page already loads, so no extra
 * endpoint (and no extra Rachio call) is needed to answer it.
 *
 * Whether water is actually moving is `activeRuns()` in @irrigation/core,
 * which owns the run-record lifecycle for every client. This file is only the
 * wording: which situation outranks which, and how to say it.
 */

import type { RunRecord, ScheduleConfig, Tenant, Zone } from '@irrigation/core';
import { activeRuns, sessionPhase, shiftDate, windowWrapsMidnight } from '@irrigation/core';
import { clockTime, fmtTime, SKIP_LABELS, toIsoDate } from './format.js';
import { isNewSeedSkip, isSeedRun, valveMinutes } from './runs.js';

export type StatusTone = 'active' | 'good' | 'warn' | 'bad' | 'muted';

export interface CurrentStatus {
  tone: StatusTone;
  /** Headline, e.g. "Watering Front Lawn". */
  title: string;
  /** One-line supporting detail. */
  detail: string;
  /**
   * True while valves are believed open (or soaking between pulses) — the
   * dashboard polls while this holds so the headline stays honest.
   */
  live: boolean;
}

/** "6 min", "under a minute", "1 h 05 min" for a future instant. */
function untilText(until: number, now: number): string {
  const min = Math.round((until - now) / 60_000);
  if (min <= 0) return 'under a minute';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

function isPaused(config: ScheduleConfig | null, now: number): boolean {
  return Boolean(config?.pausedUntil && Date.parse(config.pausedUntil) > now);
}

function zoneNameOf(zones: Zone[], zoneId: string): string {
  return zones.find((z) => z.id === zoneId)?.name ?? 'a zone';
}

/** " · and 2 more zones" style suffix for concurrent activity. */
function alsoText(count: number): string {
  if (count <= 0) return '';
  return ` · ${count} more zone${count > 1 ? 's' : ''} in this pass`;
}

export interface StatusInput {
  tenant: Tenant;
  zones: Zone[];
  config: ScheduleConfig | null;
  runs: RunRecord[];
  /** Epoch ms; injected so the caller can tick it and so this stays pure. */
  now: number;
}

/**
 * The single line of truth for the top of the dashboard. In-flight watering
 * outranks everything — pause and winterize describe what *will* happen, and a
 * user looking at the page while a valve is open needs to see that first.
 */
export function currentStatus({ tenant, zones, config, runs, now }: StatusInput): CurrentStatus {
  const active = activeRuns(runs, now);

  const watering = active.filter((a) => a.phase === 'WATERING');
  // A misting record between cycles is reported as soaking, which is the right
  // word for the minutes after a session pulse and the wrong one for the three
  // hours between mists: the valve has been shut for ages, there is nothing to
  // stop, and polling every few seconds tells the reader nothing. Those are
  // split out below and reported as what they are: waiting for the next mist.
  const soaking = active.filter((a) => a.phase === 'SOAKING' && !isSeedRun(a.run));
  const betweenMists = active.filter((a) => a.phase === 'SOAKING' && isSeedRun(a.run));

  if (watering.length > 0) {
    // A named zone we manage always wins the headline; an unmanaged watering
    // (a zone the controller ran that we never imported) only leads when it is
    // the only thing open.
    const first = watering.find((a) => !a.run.unmanaged) ?? watering[0]!;
    if (first.run.unmanaged) {
      const left =
        first.until === undefined
          ? first.run.actualStart
            ? `Started ${clockTime(first.run.actualStart)}`
            : 'Running now'
          : `About ${untilText(first.until, now)} left`;
      return {
        tone: 'active',
        title: 'Controller is watering',
        detail: `A zone not imported here · ${left}${alsoText(active.length - 1)}`,
        live: true,
      };
    }
    const name = zoneNameOf(zones, first.run.zoneId);
    const cycles = first.run.plan.cycles.length;
    const cycleNote =
      cycles > 1 && first.run.cyclesStarted ? ` · cycle ${first.run.cyclesStarted} of ${cycles}` : '';
    // An unknown end means Rachio reported a start without a duration — say
    // when it began rather than inventing a countdown.
    const left =
      first.until === undefined
        ? first.run.actualStart
          ? `Started ${clockTime(first.run.actualStart)}`
          : 'Running now'
        : `About ${untilText(first.until, now)} left`;
    return {
      tone: 'active',
      title: `Watering ${name}`,
      detail: `${left}${cycleNote}${alsoText(active.length - 1)}`,
      live: true,
    };
  }

  if (soaking.length > 0) {
    const first = soaking[0]!;
    const name = zoneNameOf(zones, first.run.zoneId);
    const cycles = first.run.plan.cycles.length;
    const nextCycle = (first.run.cyclesStarted ?? 0) + 1;
    const due =
      first.until === undefined || first.until <= now ? 'due now' : `in ${untilText(first.until, now)}`;
    return {
      tone: 'active',
      title: `Soaking ${name}`,
      detail:
        `Letting the water sink in — cycle ${Math.min(nextCycle, cycles)} of ${cycles} ${due}` +
        alsoText(active.length - 1),
      live: true,
    };
  }

  // Nothing is running: describe why, then what's next.
  if (tenant.rachio.status === 'FAILED') {
    return {
      tone: 'bad',
      title: 'Controller unreachable',
      detail: 'Rachio rejected the stored connection, so nothing can be watered.',
      live: false,
    };
  }
  if (tenant.rachio.status !== 'CONNECTED' || zones.length === 0 || !tenant.location) {
    return {
      tone: 'warn',
      title: 'Setup unfinished',
      detail: 'Watering starts once the steps below are done.',
      live: false,
    };
  }
  if (config?.winterized) {
    return {
      tone: 'muted',
      title: 'Winterized',
      detail: 'Planning and watering are suspended until you resume.',
      live: false,
    };
  }
  if (isPaused(config, now) && config) {
    return {
      tone: 'warn',
      title: 'Paused',
      detail: `Scheduled runs are skipped until ${new Date(Date.parse(config.pausedUntil!)).toLocaleDateString(
        undefined,
        { month: 'short', day: 'numeric' },
      )}.`,
      live: false,
    };
  }

  // Misting between cycles: not watering, not soaking, but not idle either.
  if (betweenMists.length > 0) {
    const first = betweenMists[0]!;
    const name = zoneNameOf(zones, first.run.zoneId);
    const due =
      first.until === undefined || first.until <= now ? 'due now' : `in ${untilText(first.until, now)}`;
    const others = betweenMists.length - 1;
    return {
      tone: 'good',
      title: `Misting ${name}`,
      detail:
        `New seed: the valve is shut between cycles. Next mist ${due}` +
        (others > 0 ? ` · ${others} more seeded zone${others > 1 ? 's' : ''}` : '') +
        '.',
      live: false,
    };
  }

  // The "current" runs belong to the session we're on — which for a window that
  // wraps midnight (or an early plan for the next night) is keyed to the date
  // the window opens, not necessarily the calendar day.
  const nowLocal = new Date(now);
  const sessionDate = config
    ? sessionPhase(toIsoDate(nowLocal), nowLocal.getHours() * 60 + nowLocal.getMinutes(), config.window)
        .sessionDate
    : toIsoDate(nowLocal);
  const sessionRuns = runs.filter((r) => r.scheduledAt.slice(0, 10) === sessionDate);
  const queued = sessionRuns.filter((r) => r.status === 'PLANNED' && r.plan.shouldWater);
  if (queued.length > 0 && config) {
    return {
      tone: 'good',
      title: 'Idle — watering queued',
      detail: `${queued.length} zone${queued.length > 1 ? 's' : ''} to water in the window starting ${fmtTime(
        config.window.earliestStart,
      )}.`,
      live: false,
    };
  }

  // Once the window closes, `sessionDate` moves on to the NEXT session, but the
  // watering that just finished is still today's news until midnight. Those runs
  // are keyed to the previous session, so fold them in whenever that session's
  // window closed on the viewer's local today.
  const todayIso = toIsoDate(nowLocal);
  const prevSessionDate = shiftDate(sessionDate, -1);
  const prevSessionClosedToday =
    config !== null &&
    (windowWrapsMidnight(config.window) ? shiftDate(prevSessionDate, 1) : prevSessionDate) === todayIso;
  const doneToday = prevSessionClosedToday
    ? runs.filter((r) => r.scheduledAt.slice(0, 10) === prevSessionDate)
    : [];
  const watered = [...sessionRuns, ...doneToday].filter(
    (r) => r.status === 'COMPLETED' && !r.dryRun && !r.unmanaged,
  );
  if (watered.length > 0) {
    const minutes = Math.round(watered.reduce((sum, r) => sum + valveMinutes(r), 0));
    return {
      tone: 'good',
      title: 'Idle — watering done for today',
      detail: `${watered.length} zone${watered.length > 1 ? 's' : ''} watered, ${minutes} min of valve time.`,
      live: false,
    };
  }

  // NEW_SEED is a hand-off to the misting scheduler, not a reason nothing was
  // watered. Saying "no watering needed: new seed misting" reads as a weather
  // call on a zone that is being watered several times a day.
  const skipped = sessionRuns.filter((r) => r.status === 'SKIPPED' && !isNewSeedSkip(r));
  if (skipped.length > 0) {
    // The skipped session may be tomorrow's (a same-day window that already
    // closed plans the next one), so name the day rather than assuming "today".
    const dayWord = sessionDate === todayIso ? 'today' : 'tomorrow';
    const reasons = [...new Set(skipped.flatMap((r) => r.plan.skips))]
      .map((s) => SKIP_LABELS[s] ?? s.toLowerCase())
      .join(', ');
    return {
      tone: 'good',
      title: 'Idle — no watering needed',
      detail: reasons
        ? `No watering planned for ${dayWord}: ${reasons}.`
        : `No watering planned for ${dayWord}. The soil still has enough moisture.`,
      live: false,
    };
  }

  return {
    tone: 'muted',
    title: 'Idle',
    detail: config
      ? `Nothing running. The planner checks the soil balance before ${fmtTime(config.window.earliestStart)}.`
      : 'Nothing running.',
    live: false,
  };
}
